/**
 * Admin API
 *
 * Admin-only endpoints for exports, deleted users, user lifecycle, and
 * analytics. Requires Firebase authentication; uses core request helpers.
 */
import { get, post, del } from '../core';
import { auth } from '../../firebase/firebase';
const adminApi = {
  /**
   * Exports user data and sends it via email to support
   * 
   * @returns {Promise<Object>} Object containing success message and user count
   */
  exportUsers: async () => {
    try {
      // Get current user from Firebase auth
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('No authenticated user found');
      }
      
      const response = await get('/admin/users/export');
      
      // Ensure we're returning the exact response data structure
      return {
        message: response?.message || 'User data exported successfully',
        userCount: response?.userCount || 0
      };
    } catch (error) {
      console.error('Error exporting users:', error);
      throw error;
    }
  },

  /**
   * Gets comprehensive user dashboard data for admin
   * 
   * @returns {Promise<Object>} Object containing user summary and detailed user data
   */
  getUserDashboard: async () => {
    try {
      console.log('AdminAPI - getUserDashboard: Starting request...');
      
      // Get current user from Firebase auth
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('No authenticated user found');
      }
      
      console.log('AdminAPI - getUserDashboard: User authenticated, making API call...');
      
      const response = await get('/admin/users/dashboard', { timeout: 25000 });
      
      console.log('AdminAPI - getUserDashboard: Raw response:', response);
      
      return response;
    } catch (error) {
      console.error('AdminAPI - getUserDashboard: Error:', error);
      throw error;
    }
  },

  /**
   * Full user list for admin Users List page (client-side filter/sort/search). Up to 2000 users.
   * @returns {Promise<{ users }>}
   */
  listUsers: async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) throw new Error('No authenticated user found');
    return get('/admin/users/list');
  },

  /**
   * Gets deleted users data for admin dashboard
   * 
   * @returns {Promise<Object>} Object containing deleted users list and summary
   */
  getDeletedUsers: async () => {
    try {
      console.log('AdminAPI - getDeletedUsers: Starting request...');
      
      // Get current user from Firebase auth
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('No authenticated user found');
      }
      
      const response = await get('/admin/deleted-users/deleted-users');
      
      console.log('AdminAPI - getDeletedUsers: Raw response:', response);
      
      return response;
    } catch (error) {
      console.error('AdminAPI - getDeletedUsers: Error:', error);
      throw error;
    }
  },

  /**
   * Gets a specific deleted user by ID
   * 
   * @param {string} id - The deleted user record ID
   * @returns {Promise<Object>} Deleted user data
   */
  getDeletedUser: async (id) => {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('No authenticated user found');
      }
      
      const response = await get(`/admin/deleted-users/deleted-users/${id}`);
      return response;
    } catch (error) {
      console.error('AdminAPI - getDeletedUser: Error:', error);
      throw error;
    }
  },

  /**
   * Search deleted users by email
   * 
   * @param {string} email - Email to search for
   * @returns {Promise<Array>} Array of matching deleted users
   */
  searchDeletedUsers: async (email) => {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('No authenticated user found');
      }
      
      const response = await get(`/admin/deleted-users/deleted-users/search/${encodeURIComponent(email)}`);
      return response;
    } catch (error) {
      console.error('AdminAPI - searchDeletedUsers: Error:', error);
      throw error;
    }
  },

  /**
   * Permanently delete a deleted user record from the database
   * 
   * @param {string} id - The deleted user record ID to remove
   * @returns {Promise<Object>} Success message
   */
  deleteDeletedUserRecord: async (id) => {
    try {
      console.log('AdminAPI - deleteDeletedUserRecord: Starting request for ID:', id);
      
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('No authenticated user found');
      }
      
      const response = await del(`/admin/deleted-users/${id}`);
      
      console.log('AdminAPI - deleteDeletedUserRecord: Success for ID:', id);
      return response;
    } catch (error) {
      console.error('AdminAPI - deleteDeletedUserRecord: Error:', error);
      throw error;
    }
  },

  /**
   * Revoke free access for a user - cancels subscription and downgrades to basic
   * 
   * @param {string} email - Email of the user to revoke access for
   * @returns {Promise<Object>} Result of the revocation
   */
  revokeUserAccess: async (email) => {
    try {
      console.log('AdminAPI - revokeUserAccess: Starting request for email:', email);
      
      // Get current user from Firebase auth
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('No authenticated user found');
      }
      
      const response = await post('/admin/free-users/revoke-access', { email });
      
      console.log('AdminAPI - revokeUserAccess: Success:', response);
      return response;
    } catch (error) {
      console.error('AdminAPI - revokeUserAccess: Error:', error);
      throw error;
    }
  },

  /**
   * Delete a user account and all associated data (admin only)
   * 
   * @param {string} uid - User ID to delete
   * @returns {Promise<Object>} Result of the deletion with statistics
   */
  deleteUser: async (uid) => {
    try {
      console.log('AdminAPI - deleteUser: Starting request for UID:', uid);
      
      // Get current user from Firebase auth
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('No authenticated user found');
      }
      
      const response = await del(`/admin/users/${uid}`);
      
      console.log('AdminAPI - deleteUser: Success:', response);
      return response;
    } catch (error) {
      console.error('AdminAPI - deleteUser: Error:', error);
      throw error;
    }
  },

  // Note: duplicate key `deleteDeletedUserRecord` removed to avoid shadowing

  /**
   * Delete an orphaned user (exists in Firestore but not in Auth)
   */
  deleteOrphanedUser: async (uid) => {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('No authenticated user found');
      }
      const response = await del(`/admin/users/orphaned/${uid}`);
      return response;
    } catch (error) {
      console.error('AdminAPI - deleteOrphanedUser: Error:', error);
      throw error;
    }
  },

  /**
   * Get analytics data for the specified time period
   * @param {string} period - Time period ('1week', '1month', '3months', '6months', '1year', 'all')
   * @returns {Promise<Object>} Analytics data with charts and summary
   */
  getAnalytics: async (period = 'all') => {
    try {
      console.log('AdminAPI - getAnalytics: Starting request for period:', period);
      
      // Get current user from Firebase auth
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('No authenticated user found');
      }
      
      const response = await get(`/admin/analytics?period=${period}`, { timeout: 25000 });
      
      console.log('AdminAPI - getAnalytics: Success:', response);
      return response;
    } catch (error) {
      console.error('AdminAPI - getAnalytics: Error:', error);
      throw error;
    }
  },

  /**
   * Get historical analytics snapshots
   * @param {string} period - Time period ('7days', '30days', '90days', '180days', '365days', 'all')
   * @param {number} limit - Maximum number of snapshots to return
   * @param {string} startDate - Start date filter (YYYY-MM-DD format)
   * @param {string} endDate - End date filter (YYYY-MM-DD format)
   * @returns {Promise<Object>} Historical analytics snapshots with metadata
   */
  getAnalyticsSnapshots: async (period = '30days', limit = 100, startDate = null, endDate = null) => {
    try {
      console.log('AdminAPI - getAnalyticsSnapshots: Starting request for period:', period);
      
      // Get current user from Firebase auth
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('No authenticated user found');
      }
      
      let url = `/admin/analytics/snapshots?period=${period}&limit=${limit}`;
      if (startDate && endDate) {
        url += `&startDate=${startDate}&endDate=${endDate}`;
      }
      
      const response = await get(url, { timeout: 30000 });
      
      console.log('AdminAPI - getAnalyticsSnapshots: Success:', response.meta);
      return response;
    } catch (error) {
      console.error('AdminAPI - getAnalyticsSnapshots: Error:', error);
      throw error;
    }
  },

  /**
   * Get analytics trends for specific metrics
   * @param {string} metric - Metric name (e.g., 'currentMRR', 'payingCustomers', 'summary.trialToPayConversionRate')
   * @param {string} period - Time period ('7days', '30days', '90days', '180days', '365days', 'all')
   * @param {string} granularity - Data granularity ('daily', 'weekly', 'monthly')
   * @returns {Promise<Object>} Trend data with statistics
   */
  getAnalyticsTrends: async (metric = 'currentMRR', period = '30days', granularity = 'daily') => {
    try {
      console.log('AdminAPI - getAnalyticsTrends: Starting request for metric:', metric, 'period:', period);
      
      // Get current user from Firebase auth
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('No authenticated user found');
      }
      
      const response = await get(
        `/admin/analytics/trends?metric=${metric}&period=${period}&granularity=${granularity}`, 
        { timeout: 30000 }
      );
      
      console.log('AdminAPI - getAnalyticsTrends: Success:', response.meta);
      return response;
    } catch (error) {
      console.error('AdminAPI - getAnalyticsTrends: Error:', error);
      throw error;
    }
  },

  /**
   * Attach a Stripe subscription to a user and sync from Stripe (admin only)
   * @param {string} uid - User ID to attach/sync
   * @param {string} subscriptionId - Stripe subscription ID (optional; if omitted, server will search by customer)
   * @param {string} customerId - Stripe customer ID (optional)
   * @returns {Promise<Object>} Result object from server including subscription info
   */
  attachAndSyncSubscription: async (uid, subscriptionId = null, customerId = null) => {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('No authenticated user found');
      }
      const body = { uid };
      if (subscriptionId) body.subscriptionId = subscriptionId;
      if (customerId) body.customerId = customerId;
      const response = await post('/admin/subscriptions/attach-and-sync', body);
      return response;
    } catch (error) {
      console.error('AdminAPI - attachAndSyncSubscription: Error:', error);
      throw error;
    }
  },

  /**
   * Cancel a user's active subscription and downgrade to basic (admin only)
   * @param {string} uid - User ID whose subscription should be cancelled
   * @returns {Promise<Object>} Result object
   */
  cancelSubscription: async (uid) => {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('No authenticated user found');
      }
      const response = await post('/admin/subscriptions/cancel', { uid });
      return response;
    } catch (error) {
      console.error('AdminAPI - cancelSubscription: Error:', error);
      throw error;
    }
  }
  ,

  /**
   * List current admins (users with isAdmin claim)
   */
  listAdmins: async () => {
    const response = await get('/admin/claims/list');
    return response?.admins || [];
  },

  /**
   * Grant admin by uid or email
   */
  grantAdmin: async ({ uid, email }) => {
    if (!uid && !email) throw new Error('Provide uid or email');
    const body = {};
    if (uid) body.uid = uid;
    if (email) body.email = email;
    const response = await post('/admin/claims/grant', body);
    return response;
  },

  /**
   * Revoke admin by uid or email
   */
  revokeAdmin: async ({ uid, email }) => {
    if (!uid && !email) throw new Error('Provide uid or email');
    const body = {};
    if (uid) body.uid = uid;
    if (email) body.email = email;
    const response = await post('/admin/claims/revoke', body);
    return response;
  }
};

export default adminApi; 